import { CountableMedicationForms, type DosageData, MedicationVolumeMap } from "@qendo/medication";
import { type ScheduleData, ScheduleFrequency, ScheduleFrequencyMap } from "@qendo/schedule";
import { isEqual } from "lodash";
import { StyleSheet } from "react-native";
import { ComboBox } from "~/components/form/combo-box/ComboBox";
import { FieldGroup } from "~/components/form/FieldGroup";
import { IntervalSelector } from "~/components/form/schedule/IntervalSelector";
import { ScheduleCycleSelector } from "~/components/form/schedule/ScheduleCycleSelector";
import { ScheduleTimeSelector } from "~/components/form/schedule/ScheduleTimeSelector";
import { WeekdaySelector } from "~/components/form/schedule/WeekdaySelector";
import { useTheme } from "~/providers/ThemeProvider";

export type ScheduleFieldProps = {
    value: ScheduleData | null | undefined;
    onUpdateSchedule: (schedule: ScheduleData) => void;
    disabled?: boolean;
    dosage?: DosageData | null | undefined;
};

const DEFAULT_PAYLOAD = {
    frequency: null,
    times: [],
    interval: 2,
    cycle: null,
    days: null,
};

export const ScheduleField = ({ value, onUpdateSchedule, disabled, dosage }: ScheduleFieldProps) => {
    const { background } = useTheme();
    const current = {
        ...DEFAULT_PAYLOAD,
        ...value,
    };

    const dosage_label: string | null = dosage?.amount
        ? CountableMedicationForms.includes(dosage.form)
            ? dosage.form
            : (MedicationVolumeMap[dosage.volume] ?? null)
        : null;

    const update = <T extends keyof ScheduleData>(key: T, val: ScheduleData[T]) => {
        const payload = {
            ...current,
            [key]: val,
        };

        if (!isEqual(payload, current)) {
            onUpdateSchedule(payload);
        }
    };

    return (
        <>
            <ComboBox<ScheduleFrequency>
                items={Object.values(ScheduleFrequency)}
                itemMap={ScheduleFrequencyMap}
                value={current.frequency}
                onItemSelected={(v: ScheduleFrequency | null) => update("frequency", v)}
                label="How often?"
                disabled={disabled}
                clearable
            />
            {(current.frequency === ScheduleFrequency.interval && (
                <IntervalSelector
                    value={current.interval}
                    onChange={(interval) => update("interval", interval)}
                    disabled={disabled}
                />
            )) ||
                (current.frequency === ScheduleFrequency.cyclical && (
                    <ScheduleCycleSelector
                        value={current.cycle}
                        onChange={(cycle) => update("cycle", cycle)}
                        disabled={disabled}
                    />
                ))}
            {[null, undefined, ScheduleFrequency.as_needed].includes(current.frequency) || (
                <FieldGroup style={[styles.time, background.secondary]} title="At what time?">
                    {current.frequency === ScheduleFrequency.specific_days && (
                        <WeekdaySelector
                            value={current.days}
                            onChange={(days) => update("days", days)}
                            disabled={disabled}
                        />
                    )}
                    <ScheduleTimeSelector
                        times={current.times}
                        onChange={(times) => update("times", times)}
                        disabled={disabled}
                        dosageLabel={dosage_label}
                    />
                </FieldGroup>
            )}
        </>
    );
};

const styles = StyleSheet.create({
    time: {
        gap: 8,
    },
});
